import moment from 'moment-timezone';
import normalizeUrl from 'normalize-url';
import some from 'lodash/some';
import stringHash from 'string-hash';
import URL from 'url-parse';

import { ALIGNMENTS, RULE_TYPES, WIDGET_TYPES } from 'enums/configuration';

const DATE_FORMAT = 'YYYY-MM-DD HH:mm';

export const getAlignment = ({ config }) => (config.alignment === ALIGNMENTS.LEFT ? ALIGNMENTS.LEFT : ALIGNMENTS.RIGHT);
export const getBorder = ({ config }) => config.border;
export const getBorderRadius = ({ config }) => config.borderRadius;
export const getChatClosed = ({ config }) => config.chatClosed;
export const getChatThrottled = ({ config }) => config.chatThrottled;
export const getCloseButton = ({ config }) => config.closeButton;
export const getCustomText = configuration => configuration.customText;
export const getDisplay = ({ config }) => config.display;
export const getFont = ({ config }) => config.font;
export const getInitialChatButton = ({ config }) => config.initialChatButton;
export const getInPercentageRollout = ({ config, cohortId }) => stringHash(cohortId) % 100 <= config.percentageRollout;
export const getIsDraggable = ({ config }) => config.isDraggable;
export const getKillswitchIsOn = ({ config }) => !config.enabled;
export const getLng = ({ config }) => config.lng;
export const getMinimizedChatButton = ({ config }) => config.minimizedChatButton;
export const getOfficeHourMessage = ({ config }) => config.officeHourMessage;
export const getOnboarding = ({ config }) => config.onboarding;
export const getOngoingChat = ({ config }) => config.ongoingChat;
export const getRules = ({ config }) => config.rules || {};
export const getSelfService = ({ config }) => config.selfService;
export const getWelcomeMessage = ({ config }) => config.welcomeMessage;
export const getZIndex = ({ config }) => config.zIndex || 2147483000;
export const isDemoSelfServiceEnabled = ({ config }) => config.demoSelfServiceEnabled;
export const isSmoochEnabled = ({ config }) => config.provider === 'SMOOCH';

export const getColors = ({ config }) => {
  let colors = {};

  if ('colors' in config && config.colors != {}) {
    colors = config.colors;
    if ((colors.brandPrimary == null) | (colors.brandPrimary == '')) {
      colors.brandPrimary = '#f3f3f3';
    }

    if ((colors.brandSecondary == null) | (colors.brandSecondary == '')) {
      colors.brandSecondary = '#ffffff';
    }
  } else {
    colors.brandPrimary = '#f3f3f3';
    colors.brandSecondary = '#ffffff';
  }

  return colors;
};

export const getDropshadowEnabled = ({ config }) => {
  if ('dropshadowEnabled' in config) {
    return config.dropshadowEnabled;
  } else {
    return false;
  }
};

export const getWidgetType = ({ config: { widgetType } }) => {
  return WIDGET_TYPES[widgetType] ? widgetType : WIDGET_TYPES.CHAT;
};

export const getRule = config => {
  const rules = getRules(config);
  let matchedRule;
  let rule;
  for (let key in rules) {
    rule = rules[key];
    if (isActiveRule(key, rule)) {
      matchedRule = { key, rule };
      break;
    }
  }
  return matchedRule;
};

export const isActiveRule = (key, rule) => {
  if (!key || !rule) {
    return false;
  }

  let ruleUrl;
  const url = URL(normalizeUrl(window.location.toString()));
  if (!url) {
    return null;
  } else {
    ruleUrl = url.toString().replace(`${url.protocol}//`, '');
  }

  switch (rule.type) {
    case RULE_TYPES.CONTAINS:
      return ruleUrl.indexOf(key) > -1;
    case RULE_TYPES.EXACT:
      return ruleUrl === key;
    case RULE_TYPES.REGEX: {
      let regex = new RegExp(key, 'gi');
      return ruleUrl.match(regex);
    }
    default:
      return false;
  }
};

export const getOnboardingEnabled = ({ config }) => {
  let onboarding = getOnboarding({ config });
  return !!onboarding && onboarding.enabled;
};

export const getWithinOfficeHours = ({ config }) => {
  const officeHours = getOfficeHours(config);
  return filterOfficeHours(officeHours, (now, start, stop) => now.isBetween(start, stop));
};

const getOfficeHours = config => {
  let officeHours = {};
  if ('officeHours' in config) {
    officeHours = config.officeHours;
  }
  return officeHours;
};

//filterFn takes now, start and stop times
const filterOfficeHours = (officeHours, filterFn) => {
  let timezone = officeHours.timezone;
  let day = moment
    .tz(timezone)
    .format('dddd')
    .toLowerCase();
  let officeHoursList = officeHours[day];
  let now = moment.tz(timezone);
  let todayDate = now.format('YYYY-MM-DD');

  return some(officeHoursList, officeHours => {
    let start = moment.tz(`${todayDate} ${officeHours.start}`, DATE_FORMAT, timezone);
    let stop = moment.tz(`${todayDate} ${officeHours.stop}`, DATE_FORMAT, timezone);

    if (!start.isValid() || !stop.isValid()) {
      throw new Error('invalid moment! check config file for timestamp syntax errors');
    }

    return filterFn(now, start, stop);
  });
};

export default {
  getAlignment,
  getBorder,
  getBorderRadius,
  getChatClosed,
  getChatThrottled,
  getCloseButton,
  getColors,
  getCustomText,
  getDisplay,
  getDropshadowEnabled,
  getFont,
  getInitialChatButton,
  getInPercentageRollout,
  getIsDraggable,
  getKillswitchIsOn,
  getLng,
  getMinimizedChatButton,
  getOfficeHourMessage,
  getOnboarding,
  getOnboardingEnabled,
  getOngoingChat,
  getRule,
  getRules,
  getSelfService,
  getWelcomeMessage,
  getWidgetType,
  getWithinOfficeHours,
  getZIndex,
  isActiveRule,
  isDemoSelfServiceEnabled,
  isSmoochEnabled,
};
